import { useEffect, useState } from "react";
import axios from "axios";
import { Key, Pencil, Check, X } from "lucide-react";
import { API } from "@/contexts/AuthContext";

/**
 * KeyTagInline — compact, inline-editable key-tag badge.
 *
 * Drops onto any FOH/tester surface where a job's physical key-tag
 * needs to be visible AND editable in one tap. Saves via the canonical
 * PATCH /api/jobs/{job_id}/key-tag endpoint. Optimistic update with
 * onSaved callback so parent components can refresh without re-fetching.
 *
 * Props:
 *  - jobId       (required) job id
 *  - initial     current key_tag_number (string|null)
 *  - onSaved     callback(newValue) after a successful PATCH
 *  - size        "sm" (default) | "lg" — lg used on the tester workspace
 *  - className   extra wrapper classes
 *  - placeholder placeholder when empty (default: "Add key tag")
 */
export default function KeyTagInline({
  jobId,
  initial,
  onSaved,
  size = "sm",
  className = "",
  placeholder = "Add key tag",
}) {
  const [value, setValue] = useState(initial || "");
  const [editing, setEditing] = useState(false);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState(null);

  useEffect(() => {
    setValue(initial || "");
  }, [initial]);

  const isLarge = size === "lg";
  const filled = (value || "").trim().length > 0;

  const save = async (next) => {
    const clean = (next ?? value ?? "").toString().trim().slice(0, 32);
    setBusy(true);
    setErr(null);
    try {
      await axios.patch(`${API}/jobs/${jobId}/key-tag`, { key_tag_number: clean });
      setValue(clean);
      setEditing(false);
      if (onSaved) onSaved(clean);
    } catch (e) {
      setErr(e?.response?.data?.detail || "Save failed");
    } finally {
      setBusy(false);
    }
  };

  const baseBadge =
    "inline-flex items-center font-mono font-bold tracking-wider rounded-md border shadow-sm";
  const filledClass = "bg-emerald-100 border-emerald-300 text-emerald-800";
  const emptyClass = "bg-slate-100 border-slate-300 text-slate-500";
  const sizeClass = isLarge ? "px-3 py-2 text-[15px]" : "px-2 py-1 text-[12px]";

  if (editing) {
    return (
      <div className={`flex items-center gap-1 ${className}`} data-testid={`key-tag-edit-${jobId}`}>
        <Key className={isLarge ? "w-4 h-4 text-slate-500" : "w-3 h-3 text-slate-500"} />
        <input
          type="text"
          value={value}
          onChange={(e) => setValue(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === "Enter") {
              e.preventDefault();
              save();
            } else if (e.key === "Escape") {
              setEditing(false);
              setValue(initial || "");
            }
          }}
          autoFocus
          maxLength={32}
          placeholder={placeholder}
          className={`font-mono uppercase tracking-wider rounded border border-slate-300 focus:border-emerald-500 focus:ring-1 focus:ring-emerald-500 outline-none ${
            isLarge ? "px-2 py-1.5 text-[14px] w-32" : "px-1.5 py-0.5 text-[12px] w-24"
          }`}
          data-testid={`key-tag-input-${jobId}`}
        />
        <button
          type="button"
          onClick={() => save()}
          disabled={busy}
          className="p-1 rounded hover:bg-emerald-50 text-emerald-700 disabled:opacity-50"
          data-testid={`key-tag-save-${jobId}`}
          aria-label="Save key tag"
        >
          <Check className={isLarge ? "w-4 h-4" : "w-3 h-3"} />
        </button>
        <button
          type="button"
          onClick={() => {
            setEditing(false);
            setValue(initial || "");
            setErr(null);
          }}
          className="p-1 rounded hover:bg-slate-100 text-slate-500"
          data-testid={`key-tag-cancel-${jobId}`}
          aria-label="Cancel"
        >
          <X className={isLarge ? "w-4 h-4" : "w-3 h-3"} />
        </button>
        {err && (
          <span
            className="text-[11px] text-red-700 ml-1"
            data-testid={`key-tag-error-${jobId}`}
          >
            {err}
          </span>
        )}
      </div>
    );
  }

  return (
    <button
      type="button"
      onClick={(e) => {
        e.stopPropagation();
        setEditing(true);
      }}
      className={`${baseBadge} ${filled ? filledClass : emptyClass} ${sizeClass} ${className} hover:ring-2 hover:ring-emerald-300 transition-shadow`}
      title={filled ? `Key tag: ${value}` : "Click to add key tag"}
      data-testid={`key-tag-badge-${jobId}`}
    >
      <Key className={isLarge ? "w-4 h-4 mr-2 opacity-70" : "w-3 h-3 mr-1 opacity-70"} />
      {filled ? value : placeholder}
      <Pencil className={isLarge ? "w-3 h-3 ml-2 opacity-50" : "w-2.5 h-2.5 ml-1 opacity-50"} />
    </button>
  );
}
